import { Assignment2Store as AssignmentStore } from "@/stores/assignment-2-store.core";
import { TaskModel } from "@bryntum/gantt-thin";
import type {
   BryntumGantt,
   BryntumGanttProjectModelProps,
   BryntumGanttProps,
} from "@bryntum/gantt-react-thin";
import type { I18njs } from "@procore/core-i18n-js";
import { TaskType } from "./prop-types";
import type { RefObject } from "react";
import { getTimeRangesFeature } from "./gantt-utils";

// We're extending the task model so we can specify that we want our "type" field to always write/appear in the task record data
// whenever the tasks are added/updated/deleted and sent to the backend through our syncUrl.
export class ProcoreTaskModel extends TaskModel {
   static get fields() {
      return [
         // referenceId, originalParentProjectId, and others will be manually added and used in the onBeforePaste event listener to support server-side record duplication
         { name: "referenceId", type: "string", alwaysWrite: true },
         { name: "originalParentProjectId", type: "string", alwaysWrite: true },
         { name: "originalParentCategoryId", type: "string", alwaysWrite: true },
         { name: "originalParentSubcategoryId", type: "string", alwaysWrite: true },
         { name: "newParentProjectId", type: "string", alwaysWrite: true },
         { name: "newParentCategoryId", type: "string", alwaysWrite: true },
         { name: "newParentSubcategoryId", type: "string", alwaysWrite: true },
         { name: "name", type: "string", alwaysWrite: true },
         { name: "type", type: "string", alwaysWrite: true },
         { name: "projectId", type: "string", alwaysWrite: true },
         { name: "categoryId", type: "string", alwaysWrite: true },
         { name: "subcategoryId", type: "string", alwaysWrite: true },
         {
            name: "resourceId",
            type: "string",
            alwaysWrite: true,
         },
         // Fields inherited from Bryntum's TaskModel that we want to block from sending updates through autoSync
         { name: "duration", type: "number", alwaysWrite: false, persist: false },
         { name: "effort", type: "number", alwaysWrite: false, persist: false },
         { name: "inactive", type: "boolean", alwaysWrite: false, persist: false },
         { name: "calendarId", type: "string", alwaysWrite: false, persist: false },
         { name: "calendar", type: "string", alwaysWrite: false, persist: false },
      ];
   }
}

// This is a new method, created to support this syncUrl feature. We typically use a store method to communicate between client and server,
// but this syncUrl param only takes the URI and handles the request to the server internally. We could have just provided the raw
// string, but I'm unsure if there's special logic in the store.core.ts request methods that change the basePath whenever we're in non-local
// environments. To be safe, I opted to put the URI construction logic into it's own abstract method, this way we know we're always providing
// the correct path to our lc-core-api server.
const completeSyncUrl = AssignmentStore.getFullRequestUrl({
   path: "/api/v3/gantt/sync",
});

// v7: trackProjectSettings is not in React wrapper types but is documented in migration guide
export const projectConfig: BryntumGanttProjectModelProps & { trackProjectSettings?: boolean } = {
   autoSync: true,
   phantomIdField: "phantomId",
   phantomParentIdField: "phantomParentId",
   // Bryntum v7 dropped credentials: "include" as a default.
   // Production is cross-origin, so we must include it to send auth cookies.
   transport: {
      sync: {
         url: completeSyncUrl,
         credentials: "include",
      },
   },
   useRawData: false,
   autoMergeAdjacentSegments: true,
   trackProjectSettings: false, // preserve v6 behavior - don't include project settings in sync
};

export const initGanttConfig = (
   ganttRef: RefObject<BryntumGantt>,
   I18n: I18njs,
): BryntumGanttProps => ({
   autoAdjustTimeAxis: false,
   barMargin: 4,
   columnLines: true,
   rowLines: true,
   baselinesFeature: false,
   dependenciesFeature: false,
   height: "calc(100vh - 250px)",
   headerMenuFeature: false,
   indicatorsFeature: false,
   percentBarFeature: false,
   projectLinesFeature: false,
   progressLineFeature: false,
   taskMenuFeature: {
      items: {
         convertToMilestone: false,
         editTask: false,
         milestoneAction: false,
         cut: false,
         filterMenu: false,
         add: false,
         indent: false,
         outdent: false,
         deleteTask: false,
         linkTasks: false,
         unlinkTasks: false,
         taskColor: false,
         splitTask: true,
         renameSegment: false,
         // We are using the onTaskMenuBeforeShow event listener to programatically update this item
         // to have context-specific text and onItem behavior
         createAssignment: {
            weight: 0,
            text: I18n.t("views.company.workforce_planning.gantt.task_menu.new_assignment"),
         },
         createRequest: {
            weight: 0,
            text: I18n.t("views.company.workforce_planning.gantt.task_menu.new_request"),
         },
         editAssignment: {
            weight: 0,
            text: I18n.t("views.company.workforce_planning.gantt.task_menu.edit_assignment"),
         },
         editRequest: {
            weight: 0,
            text: I18n.t("views.company.workforce_planning.gantt.task_menu.edit_request"),
         },
         unassign: {
            weight: 0,
            text: I18n.t("views.company.workforce_planning.gantt.task_menu.unassign"),
         },
         createAssignmentGrouping: {
            weight: 0,
            text: I18n.t("views.company.workforce_planning.gantt.task_menu.add_category"),
            separator: true,
         },
         sendAssignmentAlerts: {
            weight: 0,
            text: I18n.t("views.company.workforce_planning.gantt.task_menu.send_assignment_alerts"),
            separator: true,
         },
         deleteAssignmentGrouping: {
            weight: 0,
            text: I18n.t("views.company.workforce_planning.gantt.task_menu.delete_category"),
            separator: true,
         },
         deleteAssignment: {
            weight: 0,
            text: I18n.t("views.company.workforce_planning.gantt.task_menu.delete_assignment"),
            separator: true,
         },
         deleteRequest: {
            weight: 0,
            text: I18n.t("views.company.workforce_planning.gantt.task_menu.delete_request"),
            separator: true,
         },
      },
   },
   timeAxisHeaderMenuFeature: false,
   sortFeature: {
      toggleOnHeaderClick: false,
   },
   timeRangesFeature: getTimeRangesFeature(),
   treeFeature: true,
   onTaskDblClick: () => false,
   onBeforePaste: ({ originalRecords, records, referenceRecord: pastedNextToRecord }: any) => {
      // The records that were duplicated. These already exist in the database.
      const referenceRecords = originalRecords.flatMap((record: any) => [
         record,
         ...record.getAllChildren(),
      ]);
      // "Phantom" implies that these were generated by Bryntum with placeholder phantomId values, and have yet to be sent to the server to be saved
      const phantomDuplicateRecords = records.flatMap((record: any) => [
         record,
         ...record.getAllChildren(),
      ]);

      // Only allow user to paste tasks next to a task of the same type. This is pretty restrictive but it should ensure that that no invalid pastes occur.
      if (phantomDuplicateRecords[0].getData("type") !== pastedNextToRecord.getData("type")) {
         return false;
      }

      const getParentEntityId = (
         record: any,
         entityType: "category" | "subcategory" | "project",
      ) => {
         const parent = record.parent;
         if (!parent) return null;

         if (parent.getData("type") === entityType) {
            // Parse the bryntum phantomId's to get the valid UUID for the new duplicate record that will be inserted.
            // This method will also work for non-phantom ID's because the split method will just return an array with a single item.
            return parent.id.split("_").at(-1);
         }

         return getParentEntityId(parent, entityType);
      };

      for (const [i, phantomRecord] of Object.entries(phantomDuplicateRecords) as any) {
         phantomRecord.referenceId = referenceRecords[i].id;
         phantomRecord.originalParentProjectId = getParentEntityId(referenceRecords[i], "project");
         phantomRecord.originalParentCategoryId = getParentEntityId(
            referenceRecords[i],
            "category",
         );
         phantomRecord.newParentProjectId = getParentEntityId(phantomRecord, "project");
         phantomRecord.newParentCategoryId = getParentEntityId(phantomRecord, "category");
         phantomRecord.newParentSubcategoryId = getParentEntityId(phantomRecord, "subcategory");
      }

      return records;
   },
   taskCopyPasteFeature: {
      generateNewName: (record: any, originalRecord?: any) => {
         const store = ganttRef.current!.instance.taskStore;
         if (
            record.getData("type") === TaskType.ASSIGNMENT ||
            record.getData("type") === TaskType.EQUIPMENT_ASSIGNMENT ||
            record.getData("type") === TaskType.REQUEST ||
            record.getData("type") === TaskType.EQUIPMENT_REQUEST
         ) {
            return record.getData("name");
         }

         // For everything else, implement the default behavior from: lc-web-app/node_modules/@bryntum/grid-thin/lib/feature/base/CopyPasteBase.js
         const nameField = "name";
         let originalName = record.getValue(nameField),
            counter = 2;
         if (
            (originalRecord == null ? void 0 : originalRecord.meta.generatedName) === originalName
         ) {
            originalName = originalRecord.meta.originalName;
         }
         let newName = originalName;
         if (newName != null && newName !== "") {
            while (store.findRecord(nameField, newName)) {
               newName = `${originalName} - ${counter}`;
               counter += 1;
            }
         }
         record.meta.generatedName = newName;
         record.meta.originalName = originalName;
         return newName;
      },
   },
   visibleDate: new Date(),
   enableUndoRedoKeys: true,
});

export type ExtractGanttProp<T extends keyof BryntumGanttProps> = Exclude<
   BryntumGanttProps[T],
   string | undefined
>;
